import { jsPDF } from 'jspdf';
import autoTable, { type CellHook, type RowInput } from 'jspdf-autotable';
import HuttonsWhiteLogo from '../../img/Huttons_white.png';
import { savePdfDocument } from './savePdfDocument';

// ── Color palette (matches buyingCalculatorPdfExport.ts / mortgageLoanPdfExport.ts) ──
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];

const SUMMARY_BAR_NAVY_RGB: [number, number, number] = [15, 27, 58];
const ALERT_RED_RGB: [number, number, number] = [184, 62, 45];

// ── Huttons brand palette (poster page — matches the buying timeline page) ──
const NAVY: [number, number, number] = [0, 31, 67];        // #001F43
const ORANGE: [number, number, number] = [245, 130, 32];   // #F58220
const GOLD: [number, number, number] = [201, 164, 92];     // #C9A45C
const GREY: [number, number, number] = [120, 122, 128];
const LIGHT_LINE: [number, number, number] = [228, 230, 235];
const CARD_HEADER: [number, number, number] = [239, 242, 248];
const PEACH: [number, number, number] = [253, 240, 232];   // orange tint fill

// ── Types ───────────────────────────────────────────────────────────────────

export interface AgentInfo {
  Name?: string;
  CEANumber?: string;
  Mobile?: string;
  Email?: string;
  Photo?: string;
}

export interface SellingPdfInputs {
  propertyTab: 'hdb' | 'private';
  salePrice: number;
  outstandingLoan: number;
  cpfWithdrawal: number;
  /** SSD toggle state (private only). */
  ssdEnabled: boolean;
  /** Formatted display dates ('' when not set). */
  purchaseDate: string;
  saleDate: string;
  /** e.g. "8% (held 1 to 2 years)" (ASCII only — jsPDF's Helvetica is WinAnsi). */
  ssdRateLabel?: string;
}

export interface SellingExpenseItemPdf {
  label: string;
  amount: number;
}

/** A single milestone in the sales schedule (HDB / private timeline). */
export interface SellingScheduleRowPdf {
  num: number;
  label: string;
  /** Formatted display date. */
  date: string;
  amount?: number;
  note?: string;
}

/** One owner's slice of a joint sale, as shown in the Proceeds Split card. Cash is
 *  their share of the net proceeds; the CPF refund is their own money back in their
 *  OA; `total` is the two together. */
export interface SellingSellerPdf {
  name: string;
  sharePct: number;
  cash: number;
  cpfRefund: number;
  total: number;
}

export interface SellingPdfData {
  inputs: SellingPdfInputs;
  expenses: SellingExpenseItemPdf[];
  /** Per-seller split — only rendered for a joint sale (two or more sellers). */
  sellers?: SellingSellerPdf[];
  totalExpenses: number;
  ssd: number;
  netProceeds: number;
  schedule: SellingScheduleRowPdf[];
  /** Mirrors the on-screen "Show dates in timeline" toggle. When false the
   *  milestone cards omit their estimated dates. Defaults to true. */
  showDates?: boolean;
  profileName?: string;
  agentInfo?: AgentInfo;
}

// ── Helpers ───────────────────────────────────────────────────────────────

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const t = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(t); resolve(img); };
    img.onerror = () => { clearTimeout(t); reject(new Error('failed')); };
    img.src = url;
  });

const loadUserPhoto = async (doc: jsPDF, agent: AgentInfo, photoX: number, photoY: number, radius: number): Promise<boolean> => {
  if (!agent.Photo) return false;
  try {
    const photoPath = agent.Photo.startsWith('/') ? agent.Photo.substring(1) : agent.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const size = radius * 2;
            const px = Math.max(256, Math.round(size * 12));
            canvas.width = px;
            canvas.height = px;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            const r = px / 2;
            const maxInCircle = r * Math.SQRT2;
            const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
            const sw = Math.round(img.width * scale);
            const sh = Math.round(img.height * scale);
            ctx.beginPath();
            ctx.arc(r, r, r, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(img, (px - sw) / 2, (px - sh) / 2, sw, sh);
            const dataUrl = canvas.toDataURL('image/png');
            doc.addImage(dataUrl, 'PNG', photoX - radius, photoY - radius, size, size);
            resolve(true);
          } else {
            resolve(false);
          }
        } catch {
          resolve(false);
        }
      };
      img.onerror = () => { clearTimeout(timeout); resolve(false); };
      img.src = photoUrl;
    });
  } catch {
    return false;
  }
};

const formatCurrency = (value: number): string =>
  `$${Math.round(value).toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const signedCurrency = (value: number): string =>
  (value < 0 ? `-${formatCurrency(Math.abs(value))}` : formatCurrency(value));

const propertyTypeLabel = (i: SellingPdfInputs): string =>
  (i.propertyTab === 'hdb' ? 'HDB Resale' : 'Private Property');

// Lowercase roman numeral for the timeline milestone badges (i, ii, iii …).
// Only ever asked for single-digit milestone counts, so the map stops at x.
const romanNumeral = (n: number): string => {
  const map: [number, string][] = [[10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let rest = Math.max(1, Math.round(n));
  let out = '';
  map.forEach(([v, sym]) => {
    while (rest >= v) { out += sym; rest -= v; }
  });
  return out;
};

// ── Header (pages 2+): slim navy bar. Page indicator stamped in a final pass. ──
const addReportHeader = (doc: jsPDF, title: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...SUMMARY_BAR_NAVY_RGB);
  doc.rect(0, 0, pageWidth, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), 12, 6.5);
  doc.setTextColor(0, 0, 0);
};

// ── Footer (page-center number stamped in a final pass) ───────────────────
const addFooter = (doc: jsPDF, logoDataUrl?: string, logoAspect?: number, agentInfo?: AgentInfo) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 15;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  if (logoDataUrl && logoAspect) {
    const logoH = 7;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } else {
    doc.text('Huttons', 10, footerY + 8);
  }
  if (agentInfo?.Name) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont('helvetica', 'bold');
    doc.text(agentInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    if (agentInfo.CEANumber) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text(`CEA: ${agentInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
    }
  } else {
    doc.setTextColor(200, 200, 200);
    doc.text('Selling Report', pageWidth - 10, footerY + 8, { align: 'right' });
  }
  doc.setTextColor(0, 0, 0);
};

/** Final pass: write "PAGE x / total" in each report header and the centered
 *  page number in each footer, once the true page count is known. */
const stampPageNumbers = (doc: jsPDF) => {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    // Centered footer page number (all pages)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(`${p}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
    // Top-right page indicator (report pages only — page 1 is the cover)
    if (p > 1) {
      doc.setFontSize(7.5);
      doc.setTextColor(220, 225, 235);
      doc.text(`PAGE ${p} / ${total}`, pageWidth - 12, 6.5, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }
};

// ── Cover page ─────────────────────────────────────────────────────────────
const renderCoverPage = async (doc: jsPDF, data: SellingPdfData, logoDataUrl?: string, logoAspect?: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const agent = data.agentInfo;

  doc.setFillColor(...DARK_BG_RGB);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');

  // Landscape pages have ~87mm less height — tighten the vertical rhythm so the
  // full cover (logo → title → agent card → client → disclaimer) still fits.
  const compact = pageHeight < 250;

  let yPos = compact ? 24 : 28;
  if (logoDataUrl && logoAspect) {
    const logoH = 12;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', (pageWidth - logoW) / 2, yPos - logoH + 2, logoW, logoH);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += compact ? 8 : 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text("SINGAPORE'S LARGEST PRIVATE REAL ESTATE AGENCY", pageWidth / 2, yPos, { align: 'center' });

  yPos += compact ? 8 : 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 45, yPos, 90, 1.2, 'F');

  yPos += compact ? 18 : 22;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('SELLING REPORT', pageWidth / 2, yPos, { align: 'center' });
  yPos += compact ? 9 : 10;
  doc.setTextColor(...ORANGE_RGB);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(propertyTypeLabel(data.inputs), pageWidth / 2, yPos, { align: 'center' });
  yPos += compact ? 12 : 16;

  // Agent info card
  if (agent) {
    const infoBoxW = 120;
    const infoBoxH = compact ? 46 : 50;
    const infoBoxX = (pageWidth - infoBoxW) / 2;
    const infoBoxY = yPos;

    doc.setFillColor(...INFO_BOX_BG_RGB);
    doc.setDrawColor(...ORANGE_RGB);
    doc.setLineWidth(2);
    doc.roundedRect(infoBoxX, infoBoxY, infoBoxW, infoBoxH, 3, 3, 'FD');

    const photoRadius = 18;
    const photoPadding = 10;
    const photoX = infoBoxX + photoPadding + photoRadius;
    const photoY = infoBoxY + infoBoxH / 2;

    const photoLoaded = await loadUserPhoto(doc, agent, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const initials = agent.Name
        ? agent.Name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
        : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }

    const textX = infoBoxX + photoPadding + photoRadius * 2 + 15;
    let detailY = infoBoxY + 12;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    if (agent.Name) { doc.text(agent.Name, textX, detailY); detailY += 7; }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (agent.CEANumber) {
      const label = 'CEA Registration: ';
      doc.setTextColor(255, 255, 255);
      doc.text(label, textX, detailY);
      doc.setTextColor(...ORANGE_RGB);
      doc.setFont('helvetica', 'bold');
      doc.text(agent.CEANumber, textX + doc.getTextWidth(label), detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      detailY += 6;
    }
    if (agent.Email) { doc.text(agent.Email, textX, detailY); detailY += 6; }
    if (agent.Mobile) { doc.text(agent.Mobile, textX, detailY); }

    yPos = infoBoxY + infoBoxH + (compact ? 10 : 14);
  } else {
    yPos += 8;
  }

  // Client name
  if (data.profileName && data.profileName.trim() !== '') {
    yPos += compact ? 8 : 10;
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Specially Prepared for', pageWidth / 2, yPos, { align: 'center' });
    yPos += 9;
    doc.setTextColor(...ORANGE_RGB);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(data.profileName.trim(), pageWidth / 2, yPos, { align: 'center' });
    yPos += compact ? 10 : 12;
  }

  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${genDate}`, pageWidth / 2, yPos, { align: 'center' });

  yPos += compact ? 8 : 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 0.8, 'F');

  yPos += compact ? 8 : 10;
  doc.setTextColor(160, 160, 160);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  const disclaimer = 'This report is for informational purposes only. Actual proceeds, fees and timelines may vary. Subject to HDB / bank processing, CPF Board rules and IRAS stamp duty regulations.';
  doc.text(doc.splitTextToSize(disclaimer, pageWidth - 50), pageWidth / 2, yPos, { align: 'center' });

  addFooter(doc, logoDataUrl, logoAspect, agent);
};

// ── Sales Proceeds & Timeline (landscape poster page — buying-report style) ──
const renderProceedsTimelinePage = (
  doc: jsPDF,
  data: SellingPdfData,
  logoDataUrl?: string,
  logoAspect?: number,
): void => {
  const i = data.inputs;
  const isHdb = i.propertyTab === 'hdb';
  const showDates = data.showDates ?? true;
  const cur = formatCurrency;

  doc.addPage('a4', 'landscape');
  addReportHeader(doc, isHdb ? 'HDB Sale Timeline' : 'Private Sale Timeline');
  addFooter(doc, logoDataUrl, logoAspect, data.agentInfo);

  // Reset stroke width — the cover page's photo circle leaves it at 2mm.
  doc.setLineWidth(0.2);

  const pageWidth = doc.internal.pageSize.getWidth();    // ~297
  const M = 10;
  const innerW = pageWidth - M * 2;

  // ── Title block ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...ORANGE);
  doc.text('ASSET PROGRESSION & DIVESTMENT', M, 14);
  doc.setFontSize(16);
  doc.setTextColor(...NAVY);
  doc.text('Estimated Sales Proceeds & Timeline', M, 21);
  doc.setFillColor(...ORANGE);
  doc.rect(M, 23.5, 36, 0.9, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(M + 37, 23.7, 12, 0.55, 'F');

  // ── KPI cards row ──
  const kpiY = 26, kpiH = 17, kpiGap = 4;
  const kpiW = (innerW - kpiGap * 3) / 4;
  const kpiCard = (
    idx: number, label: string, value: string, sub: string,
    variant: 'navy' | 'accent' | 'plain',
    valueRed = false,
  ) => {
    const x = M + idx * (kpiW + kpiGap);
    if (variant === 'navy') {
      doc.setFillColor(...NAVY);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 1.5, 1.5, 'F');
    } else {
      doc.setDrawColor(...LIGHT_LINE);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 1.5, 1.5, 'FD');
      if (variant === 'accent') {
        doc.setFillColor(...ORANGE);
        doc.rect(x, kpiY, kpiW, 1.4, 'F');
      }
    }
    const valueColor: [number, number, number] = valueRed
      ? ALERT_RED_RGB
      : variant === 'navy' ? [255, 255, 255] : variant === 'accent' ? ORANGE : NAVY;
    const captionColor: [number, number, number] = variant === 'navy' ? [190, 198, 212] : GREY;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.6);
    doc.setTextColor(...(variant === 'navy' ? GOLD : GREY));
    doc.text(label, x + 4, kpiY + 5.4);
    doc.setFontSize(11.5);
    doc.setTextColor(...valueColor);
    doc.text(value, x + 4, kpiY + 11.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.6);
    doc.setTextColor(...captionColor);
    doc.text(sub, x + 4, kpiY + 15);
  };

  const shortfall = data.netProceeds < 0;
  kpiCard(0, 'SALE PRICE', cur(i.salePrice), propertyTypeLabel(i), 'navy');
  kpiCard(1, 'OUTSTANDING LOAN', cur(i.outstandingLoan), 'To be fully redeemed on sale', 'plain');
  kpiCard(2, 'CPF REFUND', cur(i.cpfWithdrawal), 'Incl. accrued interest, back to CPF OA', 'plain');
  kpiCard(3, 'NET SALES PROCEEDS', signedCurrency(data.netProceeds),
    shortfall ? 'Shortfall — cash top-up required' : 'Est. cash proceeds after all deductions',
    'accent', shortfall);

  // ── Section header helper (numbered navy badge) ──
  const sectionHead = (n: number, title: string, sub: string, x: number, y: number) => {
    const r = 2.8;
    doc.setFillColor(...NAVY);
    doc.circle(x + r, y, r, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.3);
    doc.setTextColor(255, 255, 255);
    doc.text(String(n), x + r, y + 1.1, { align: 'center' });
    doc.setFontSize(9.7);
    doc.setTextColor(...NAVY);
    doc.text(title, x + r * 2 + 3, y + 1.35);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.6);
    doc.setTextColor(...GREY);
    doc.text(sub, x + r * 2 + 3, y + 5);
  };

  // ── Section 1: Sales Timeline (milestone cards) ──
  const s1Y = kpiY + kpiH + 6;
  sectionHead(1, 'Sales Timeline', 'Sequence of milestones from option to key handover.', M, s1Y);

  const cards = data.schedule;
  const cardsTop = s1Y + 6.5;
  const cardGap = 2;
  const cardW = (innerW - cardGap * (cards.length - 1)) / Math.max(1, cards.length);
  const cardH = 29;
  const cardHeaderH = 8.6;
  cards.forEach((c, idx) => {
    const highlight = idx === cards.length - 1;
    const x = M + idx * (cardW + cardGap);
    if (highlight) {
      doc.setFillColor(...PEACH);
      doc.setDrawColor(...ORANGE);
    } else {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...LIGHT_LINE);
    }
    doc.roundedRect(x, cardsTop, cardW, cardH, 1.4, 1.4, 'FD');

    // Header band behind the milestone title, matching the on-screen timeline cards.
    doc.setFillColor(...(highlight ? PEACH : CARD_HEADER));
    doc.roundedRect(x, cardsTop, cardW, cardHeaderH, 1.4, 1.4, 'F');
    doc.rect(x, cardsTop + cardHeaderH - 1.4, cardW, 1.4, 'F');
    doc.setDrawColor(...(highlight ? ORANGE : LIGHT_LINE));
    doc.setLineWidth(0.1);
    doc.line(x, cardsTop + cardHeaderH, x + cardW, cardsTop + cardHeaderH);

    // number badge
    const br = 2.5;
    const bcx = x + 3.4 + br;
    doc.setFillColor(...(highlight ? ORANGE : NAVY));
    doc.circle(bcx, cardsTop + cardHeaderH / 2, br, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.4);
    doc.setTextColor(255, 255, 255);
    // Roman numerals are lowercase, so they sit lower than digits — a smaller
    // baseline offset keeps the glyph optically centred in the badge.
    doc.text(romanNumeral(idx + 1), bcx, cardsTop + cardHeaderH / 2 + 0.6, { align: 'center' });
    // title — clamp to the header band (2 lines max)
    doc.setFontSize(5.4);
    doc.setTextColor(...NAVY);
    const titleLines = (doc.splitTextToSize(c.label.toUpperCase(), cardW - 11) as string[]).slice(0, 2);
    doc.text(titleLines, bcx + br + 1.5, cardsTop + (titleLines.length > 1 ? 3.4 : 4.6));
    // date — always an estimate, so prefix "Est." Suppressed when the seller
    // hides timeline dates; the amount block then moves up into its place.
    if (showDates) {
      doc.setFontSize(7.6);
      doc.setTextColor(...NAVY);
      doc.text(c.date ? `Est. ${c.date}` : '-', x + 3.4, cardsTop + 12.2);
    }
    // Amount received at this milestone — no-payment milestones stay blank
    // (no label, no dash) so the card doesn't advertise a meaningless figure.
    if (c.amount != null && c.amount > 0) {
      const labelY = cardsTop + (showDates ? 15.4 : 12.6);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(4.6);
      doc.setTextColor(...GREY);
      doc.text('AMOUNT RECEIVED', x + 3.4, labelY);
      doc.setFontSize(9);
      doc.setTextColor(...ORANGE);
      doc.text(cur(c.amount), x + 3.4, labelY + 4.2);
    }
    // hairline
    doc.setDrawColor(...(highlight ? [242, 214, 190] as [number, number, number] : LIGHT_LINE));
    doc.setLineWidth(0.15);
    doc.line(x + 3.4, cardsTop + 21.4, x + cardW - 3.4, cardsTop + 21.4);
    // note — clamp so long HDB notes never overflow the card
    if (c.note) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(4.0);
      doc.setTextColor(110, 112, 118);
      const noteLines = (doc.splitTextToSize(c.note, cardW - 6.5) as string[]).slice(0, 3);
      doc.text(noteLines, x + 3.4, cardsTop + 23.9);
    }
  });

  // ── Phase bands ──
  const bandY = cardsTop + cardH + 1.5;
  const bandH = 5.5;
  const splitIdx = Math.max(1, cards.length - 1);
  const splitX = M + splitIdx * (cardW + cardGap);
  doc.setFillColor(...NAVY);
  doc.rect(M, bandY, splitX - cardGap - M, bandH, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(splitX, bandY, M + innerW - splitX, bandH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.0);
  const band1 = isHdb ? 'OTP TO HDB APPROVAL' : 'OPTION TO EXERCISE (~2 WEEKS)';
  const band2 = isHdb ? 'KEY HANDOVER' : 'COMPLETION (~12 WEEKS)';
  doc.text(band1, (M + splitX - cardGap) / 2, bandY + bandH / 2 + 1.2, { align: 'center' });
  doc.setFontSize(5.8);
  doc.text(band2, (splitX + M + innerW) / 2, bandY + bandH / 2 + 1.2, { align: 'center' });

  // ── Lower two columns ──
  const leftX = M;
  const colGap2 = 8;
  const leftW2 = 134;
  const rightX = M + leftW2 + colGap2;
  const rightW2 = innerW - leftW2 - colGap2;
  const lowerY = bandY + bandH + 4;

  const lastY = () => (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? lowerY;
  // Left-align foot labels; paint deduction rows ("- $x") orange.
  const deductionHook: CellHook = (h) => {
    if (h.section === 'foot' && h.column.index === 0) h.cell.styles.halign = 'left';
    if ((h.section === 'body' || h.section === 'foot')
      && typeof h.cell.raw === 'string' && h.cell.raw.trim().startsWith('-')) {
      h.cell.styles.textColor = ORANGE;
    }
  };

  // Sized so the two lower columns fill the page without ever spilling onto a
  // second sheet — see the section layout below for the worst-case budget.
  const gridTable = (opts: {
    startY: number; x: number; width: number;
    head: RowInput[]; body: RowInput[]; foot?: RowInput[];
    footRed?: boolean; didParseCell?: CellHook;
    columnStyles?: Record<number, Record<string, unknown>>;
  }) => {
    autoTable(doc, {
      startY: opts.startY,
      margin: { left: opts.x, bottom: 18 },
      tableWidth: opts.width,
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 8.2, cellPadding: { top: 1.9, bottom: 1.9, left: 3, right: 3 }, lineColor: LIGHT_LINE, lineWidth: 0.1 },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold', halign: 'right' },
      columnStyles: opts.columnStyles ?? {
        0: { textColor: [60, 62, 70], halign: 'left' },
        1: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
      },
      head: opts.head,
      body: opts.body,
      foot: opts.foot,
      footStyles: {
        fillColor: PEACH,
        textColor: opts.footRed ? ALERT_RED_RGB : ORANGE,
        fontStyle: 'bold',
        halign: 'right',
        fontSize: 8.4,
      },
      didParseCell: opts.didParseCell ?? deductionHook,
    });
    return lastY();
  };

  // ── Lower sections ────────────────────────────────────────────────────────
  // Numbered in placement order: the two fixed blocks head each column, then the
  // optional ones fill whichever column is shorter.
  const sellers = data.sellers ?? [];
  const hasSplit = sellers.length > 1;
  const hasSsd = !isHdb && i.ssdEnabled;

  const renderProceedsBreakdown = (x: number, width: number, y: number, n: number) => {
    sectionHead(n, 'Proceeds Breakdown', 'From sale price to estimated net proceeds.', x, y);
    const proceedsBody: RowInput[] = [
      ['Sale Price', cur(i.salePrice)],
      ['Less: Outstanding Loan Redemption', `- ${cur(i.outstandingLoan)}`],
      ['Less: CPF Refund (w/ Accrued Interest)', `- ${cur(i.cpfWithdrawal)}`],
      ['Less: Total Selling Expenses', `- ${cur(data.totalExpenses)}`],
    ];
    if (data.ssd > 0) {
      proceedsBody.push(['Less: Seller Stamp Duty', `- ${cur(data.ssd)}`]);
    }
    return gridTable({
      startY: y + HEAD_H,
      x,
      width,
      head: [[{ content: '', styles: { halign: 'left' as const } }, 'AMOUNT']],
      body: proceedsBody,
      foot: [[
        shortfall ? 'Net Sales Proceeds (Shortfall)' : 'Net Sales Proceeds',
        signedCurrency(data.netProceeds),
      ]],
      footRed: shortfall,
    });
  };

  const renderExpenses = (x: number, width: number, y: number, n: number) => {
    sectionHead(n, 'Expenses Breakdown', 'Costs deducted from the sale price.', x, y);
    const expensesBody: RowInput[] = data.expenses.length > 0
      ? data.expenses.map((e) => [e.label, cur(e.amount)])
      : [['No selling expenses entered', '-']];
    return gridTable({
      startY: y + HEAD_H,
      x,
      width,
      head: [[{ content: '', styles: { halign: 'left' as const } }, 'AMOUNT']],
      body: expensesBody,
      foot: [['Total Selling Expenses', cur(data.totalExpenses)]],
    });
  };

  const renderSsd = (x: number, width: number, y: number, n: number) => {
    sectionHead(n, 'Seller Stamp Duty', 'Holding period determines the SSD rate (IRAS).', x, y);
    return gridTable({
      startY: y + HEAD_H,
      x,
      width,
      head: [],
      body: [
        ['Purchase Date', i.purchaseDate || '-'],
        ['Sale Date', i.saleDate || '-'],
        ['SSD Rate', i.ssdRateLabel ?? '-'],
        [
          { content: 'Seller Stamp Duty', styles: { fontStyle: 'bold' as const, textColor: NAVY } },
          { content: cur(data.ssd), styles: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: ORANGE } },
        ],
      ],
    });
  };

  // One row per owner rather than a block each — it reads as a comparison and keeps
  // the table short enough that it can never break across pages.
  const renderSplit = (x: number, width: number, y: number, n: number) => {
    sectionHead(
      n,
      'Proceeds Split',
      'CPF returns to each owner\u2019s own OA; the cash left is divided by their shares.',
      x,
      y,
    );
    const totalCash = sellers.reduce((sum, seller) => sum + seller.cash, 0);
    const totalCpf = sellers.reduce((sum, seller) => sum + seller.cpfRefund, 0);
    return gridTable({
      startY: y + HEAD_H,
      x,
      width,
      head: [[
        { content: 'OWNER', styles: { halign: 'left' as const } },
        'CASH', 'CPF REFUND', 'TOTAL',
      ]],
      body: sellers.map((seller) => [
        `${seller.name} (${Number(seller.sharePct.toFixed(2))}%)`,
        signedCurrency(seller.cash),
        cur(seller.cpfRefund),
        signedCurrency(seller.total),
      ]),
      foot: [['All Owners', signedCurrency(totalCash), cur(totalCpf), signedCurrency(totalCash + totalCpf)]],
      footRed: shortfall,
      columnStyles: {
        0: { textColor: [60, 62, 70], halign: 'left', fontStyle: 'bold' },
        1: { halign: 'right', fontStyle: 'bold', textColor: ORANGE },
        2: { halign: 'right', textColor: NAVY },
        3: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
      },
    });
  };

  // Place the two fixed blocks side by side, then drop each optional block into
  // whichever column is currently shorter — that balances the page and keeps every
  // table on this sheet. Heights are estimated from autoTable's own metrics
  // (text height = fontSize / 2.8346 x 1.15, plus the cell padding and hairline).
  const BOTTOM = doc.internal.pageSize.getHeight() - 18;
  const BLOCK_GAP = 4;
  const HEAD_H = 6.5;
  const blockHeight = (rows: number, withHead: boolean, withFoot: boolean) =>
    HEAD_H + (withHead ? 6.7 : 0) + rows * 7.2 + (withFoot ? 7.3 : 0);

  let leftY = renderProceedsBreakdown(leftX, leftW2, lowerY, 2);
  let rightY = renderExpenses(rightX, rightW2, lowerY, 3);
  let sectionNo = 4;

  const placeBlock = (
    render: (x: number, width: number, y: number, n: number) => number,
    needed: number,
  ) => {
    // Shorter column first; switch only if it would overrun and the other fits.
    let useLeft = leftY <= rightY;
    const start = () => (useLeft ? leftY : rightY) + BLOCK_GAP;
    if (start() + needed > BOTTOM) {
      const other = (useLeft ? rightY : leftY) + BLOCK_GAP;
      if (other + needed <= BOTTOM) useLeft = !useLeft;
    }
    const end = render(
      useLeft ? leftX : rightX,
      useLeft ? leftW2 : rightW2,
      start(),
      sectionNo++,
    );
    if (useLeft) leftY = end; else rightY = end;
  };

  if (hasSsd) placeBlock(renderSsd, blockHeight(4, false, false));
  if (hasSplit) placeBlock(renderSplit, blockHeight(sellers.length, true, true));
};

// ── Export filename: "<profile name> - Selling Calculator - <date time>.pdf" ─
const sanitizeFilePart = (name?: string): string =>
  (name ?? '').trim().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();

const buildExportFilename = (data: SellingPdfData): string => {
  const who = sanitizeFilePart(data.profileName);
  // ":" is not filename-safe (Windows) — use dots in the time instead.
  const ts = new Date().toLocaleString('en-SG', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).replace(/:/g, '.');
  return `${[who, 'Selling Calculator', ts].filter(Boolean).join(' - ')}.pdf`;
};

// ── Public API ───────────────────────────────────────────────────────────
export const exportSellingToPDF = async (
  data: SellingPdfData,
  filename?: string,
): Promise<void> => {
  // Landscape throughout — matches the buying report.
  // compress: the Huttons logo is a 4210x1035 PNG that jsPDF embeds as raw RGBA —
  // 17 MB of a 17 MB report. Flate-compressing the streams brings the same file to ~50 KB.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });

  let logoDataUrl: string | undefined;
  let logoAspect: number | undefined;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    logoDataUrl = HuttonsWhiteLogo;
    logoAspect = logoImg.width / logoImg.height;
  } catch { /* fallback to text */ }

  await renderCoverPage(doc, data, logoDataUrl, logoAspect);
  renderProceedsTimelinePage(doc, data, logoDataUrl, logoAspect);

  stampPageNumbers(doc);

  await savePdfDocument(doc, filename ?? buildExportFilename(data));
};
